﻿import {
  CapabilityCategory,
  NavigationalCapabilityBase,
} from '@trimble/web3d-capabilities';
import { detectArrayChanges } from '@trimble/utilities';
import {
  Accessor,
  createEffect,
  createSignal,
  getOwner,
  on,
  runWithOwner,
} from 'solid-js';
import { CapabilityCoordinatorBase } from './capability-coordinator-base';
import { CapabilityRegistryContainer } from './capability-registry/capability-registry-container';

export class NavigationCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  public readonly activeNavigationCapability: Accessor<NavigationalCapabilityBase>;

  public resync: (() => void) | null = null;

  constructor(
    registryContainer: CapabilityRegistryContainer,
    private readonly defaultCapability: NavigationalCapabilityBase
  ) {
    super(registryContainer);

    // Implemented as signal instead of memo so that we ensure consistent state -
    // there is always only one active navigation capability.
    const [activeNavCap, setActiveNavCap] =
      createSignal<NavigationalCapabilityBase>(this.defaultCapability);
    this.activeNavigationCapability = activeNavCap;

    this.defaultCapability.enable();
    this.defaultCapability.activate();

    const fallbackToDefault = (): void => {
      this.defaultCapability.enable();
      this.defaultCapability.activate();
      setActiveNavCap(this.defaultCapability);
      this.resync?.();
    };

    const owner = getOwner();

    createEffect(
      on(
        () =>
          registryContainer
            .capabilities()
            .filter(
              (c) => c.category === CapabilityCategory.Navigation
            ) as NavigationalCapabilityBase[],
        (capabilities, prevCapabilities) => {
          const additions = detectArrayChanges(
            prevCapabilities,
            capabilities
          ).newItems;

          additions.forEach((cap) => {
            runWithOwner(owner, () => {
              createEffect(
                on(cap.isActive, (isActive) => {
                  if (isActive) {
                    setActiveNavCap(cap);
                  } else if (activeNavCap() === cap) {
                    fallbackToDefault();
                  }
                })
              );
            });
          });
        }
      )
    );
  }

  public cleanup(): Promise<void> {
    this.resync = null;
    return Promise.resolve();
  }
}
